import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Zap, Pause, Play, XCircle, RefreshCw, ChevronRight, User, Building, Clock,
  CheckCircle2, AlertCircle, Mail, MessageSquare, FlaskConical,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function timeUntil(iso) {
  if (!iso) return "";
  const s = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
  if (s < 0) return "due now";
  if (s < 60) return `in ${s}s`;
  if (s < 3600) return `in ${Math.floor(s / 60)}m`;
  if (s < 86400) return `in ${Math.floor(s / 3600)}h`;
  return `in ${Math.floor(s / 86400)}d`;
}

const STATUS_COLORS = {
  active:    "bg-green-50 border-green-200 text-green-800",
  paused:    "bg-amber-50 border-amber-200 text-amber-800",
  completed: "bg-neutral-50 border-neutral-200 text-neutral-600",
  cancelled: "bg-red-50 border-red-200 text-red-700",
};

export default function Sequences() {
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [processing, setProcessing] = useState(false);
  const [testFireTemplate, setTestFireTemplate] = useState(null); // template obj
  const [testForm, setTestForm] = useState({ target_id: "", step_index: -1 });
  const [firing, setFiring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: r }] = await Promise.all([
        api.get("/sequences/templates"),
        api.get("/sequences/runs"),
      ]);
      setTemplates(t.templates || []);
      setRuns(r.runs || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return runs;
    return runs.filter((r) => r.status === filter);
  }, [runs, filter]);

  const stats = useMemo(() => {
    const total = runs.length;
    const active = runs.filter((r) => r.status === "active").length;
    const paused = runs.filter((r) => r.status === "paused").length;
    const completed = runs.filter((r) => r.status === "completed").length;
    return { total, active, paused, completed };
  }, [runs]);

  const controlRun = async (runId, action) => {
    try {
      await api.post(`/sequences/runs/${runId}/${action}`);
      toast.success(`Run ${action}d.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const processDue = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/sequences/process-due");
      toast.success(`Processed ${data.processed} due step${data.processed === 1 ? "" : "s"}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProcessing(false); }
  };

  const openTestFire = (tpl) => {
    setTestForm({ target_id: "", step_index: tpl.steps.length - 1 });
    setTestFireTemplate(tpl);
  };

  const submitTestFire = async (e) => {
    e?.preventDefault();
    if (!testFireTemplate) return;
    const target_id = testForm.target_id.trim();
    if (!target_id) { toast.error(`Enter a ${testFireTemplate.scope} ID.`); return; }
    setFiring(true);
    try {
      const { data } = await api.post("/sequences/test-fire", {
        template_key: testFireTemplate.key,
        target_id,
        step_index: testForm.step_index,
      });
      toast.success(data.message || "Test-fire scheduled in 60s.");
      setTestFireTemplate(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFiring(false); }
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="sequences-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> Automation
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">Follow-up sequences</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            Multi-touch email cadences that fire automatically. Enroll a lead or client from their detail page — the scheduler handles the rest.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="sequences-process-due-btn"
            onClick={processDue}
            disabled={processing}
            className="lf-btn-outline text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${processing ? "animate-spin" : ""}`} />
            {processing ? "Processing…" : "Process due now"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Enrolled" value={stats.total} testId="seq-stat-total" />
        <Stat label="Active" value={stats.active} accent="green" testId="seq-stat-active" />
        <Stat label="Paused" value={stats.paused} accent="amber" testId="seq-stat-paused" />
        <Stat label="Completed" value={stats.completed} testId="seq-stat-completed" />
      </div>

      {/* Template library */}
      <div className="mb-10">
        <div className="overline mb-4">Available templates</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div
              key={t.key}
              data-testid={`sequences-template-${t.key}`}
              className="border border-neutral-200 bg-white p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="font-semibold text-neutral-900">{t.name}</div>
                <span className={`text-xs px-2 py-0.5 font-mono uppercase tracking-wider ${t.scope === "lead" ? "bg-blue-50 border border-blue-200 text-blue-700" : "bg-purple-50 border border-purple-200 text-purple-700"}`}>
                  {t.scope}
                </span>
              </div>
              <p className="text-sm text-neutral-600 mb-3 leading-relaxed">{t.description}</p>
              <div className="text-xs text-neutral-400 font-mono mb-3">
                {t.steps.length} step{t.steps.length === 1 ? "" : "s"} · {t.steps.map((s) => `day ${Math.round(s.delay_hours / 24)}`).join(" → ")}
              </div>
              <button
                data-testid={`sequences-test-fire-${t.key}`}
                onClick={() => openTestFire(t)}
                className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 border border-neutral-300 text-neutral-700 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 font-mono uppercase tracking-wider"
              >
                <FlaskConical className="w-3 h-3" /> Test-fire in 60s
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4 text-sm">
        {["all", "active", "paused", "completed", "cancelled"].map((k) => (
          <button
            key={k}
            data-testid={`sequences-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 border ${filter === k ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}
          >
            {k[0].toUpperCase() + k.slice(1)} {filter === k && stats[k] !== undefined ? `(${stats[k]})` : ""}
          </button>
        ))}
      </div>

      {/* Runs table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
            <tr>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Template</th>
              <th className="text-left px-4 py-3">Progress</th>
              <th className="text-left px-4 py-3">Next / Last</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-neutral-400" data-testid="sequences-empty">
                No sequence runs {filter !== "all" ? `matching "${filter}"` : ""} yet. Enroll a lead or client to get started.
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3">
                  <Link
                    to={r.target_type === "lead" ? `/leads/${r.target_id}` : `/clients/${r.target_id}`}
                    className="inline-flex items-center gap-1.5 font-medium hover:underline"
                    data-testid={`seq-run-target-${r.id.slice(0, 8)}`}
                  >
                    {r.target_type === "lead" ? <User className="w-3.5 h-3.5 text-blue-600" /> : <Building className="w-3.5 h-3.5 text-purple-600" />}
                    {r.target_label || "(unnamed)"}
                  </Link>
                  <div className="text-xs text-neutral-400 font-mono mt-0.5">{r.target_type}</div>
                </td>
                <td className="px-4 py-3">{r.template_name}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs">
                    step {r.current_step + (r.status === "completed" ? 0 : 1)} / {r.total_steps}
                  </span>
                  <div className="w-24 h-1 bg-neutral-200 mt-1">
                    <div
                      className={`h-full ${r.status === "completed" ? "bg-neutral-400" : "bg-amber-500"}`}
                      style={{ width: `${(r.current_step / r.total_steps) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-600">
                  {r.status === "active" && (
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {timeUntil(r.next_run_at)}</span>
                  )}
                  {r.status === "paused" && (
                    <span className="text-amber-700">paused {timeAgo(r.paused_at)}</span>
                  )}
                  {(r.status === "completed" || r.status === "cancelled") && (
                    <span className="text-neutral-400">done {timeAgo(r.completed_at)}</span>
                  )}
                  {r.history && r.history.length > 0 && (
                    <div className="text-xs text-neutral-400 font-mono mt-0.5">
                      last: {r.history[r.history.length - 1].ok ? (
                        <span className="text-green-700 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> sent</span>
                      ) : (
                        <span className="text-red-700 inline-flex items-center gap-0.5" title={r.history[r.history.length - 1].error}><AlertCircle className="w-3 h-3" /> failed</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 text-xs font-mono uppercase tracking-wider border ${STATUS_COLORS[r.status] || ""}`} data-testid={`seq-run-status-${r.id.slice(0, 8)}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    {r.status === "active" && (
                      <button onClick={() => controlRun(r.id, "pause")} data-testid={`seq-pause-${r.id.slice(0, 8)}`} className="text-neutral-500 hover:text-amber-700 inline-flex items-center gap-1 text-xs">
                        <Pause className="w-3 h-3" /> Pause
                      </button>
                    )}
                    {r.status === "paused" && (
                      <button onClick={() => controlRun(r.id, "resume")} data-testid={`seq-resume-${r.id.slice(0, 8)}`} className="text-green-700 hover:text-green-900 inline-flex items-center gap-1 text-xs">
                        <Play className="w-3 h-3" /> Resume
                      </button>
                    )}
                    {(r.status === "active" || r.status === "paused") && (
                      <button onClick={() => controlRun(r.id, "cancel")} data-testid={`seq-cancel-${r.id.slice(0, 8)}`} className="text-neutral-500 hover:text-red-700 inline-flex items-center gap-1 text-xs">
                        <XCircle className="w-3 h-3" /> Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Test-fire modal */}
      {testFireTemplate && (
        <div
          data-testid="test-fire-modal"
          onClick={() => !firing && setTestFireTemplate(null)}
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitTestFire}
            className="bg-white w-full max-w-md border border-neutral-900 shadow-2xl"
          >
            <div className="bg-neutral-900 text-white px-5 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Test-fire</div>
                <div className="font-bold mt-1">{testFireTemplate.name}</div>
                <div className="text-xs text-neutral-400 mt-0.5">Scope: {testFireTemplate.scope} · fires in 60 seconds</div>
              </div>
              <button type="button" onClick={() => !firing && setTestFireTemplate(null)} className="text-neutral-400 hover:text-white" data-testid="test-fire-close">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 grid gap-4 text-sm">
              <label className="grid gap-1">
                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                  {testFireTemplate.scope === "lead" ? "Lead ID" : "Client ID"} · required
                </span>
                <input
                  required
                  autoFocus
                  value={testForm.target_id}
                  onChange={(e) => setTestForm({ ...testForm, target_id: e.target.value })}
                  placeholder="uuid…"
                  data-testid="test-fire-target-id"
                  className="border border-neutral-300 px-3 py-2 font-mono text-xs"
                />
                <span className="text-xs text-neutral-500">
                  Grab this from the {testFireTemplate.scope} detail URL — it's a valid record with a phone/email.
                </span>
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Step to fire</span>
                <select
                  value={testForm.step_index}
                  onChange={(e) => setTestForm({ ...testForm, step_index: parseInt(e.target.value, 10) })}
                  data-testid="test-fire-step-index"
                  className="border border-neutral-300 px-3 py-2"
                >
                  {testFireTemplate.steps.map((s, i) => (
                    <option key={`${s.channel}-${s.delay_hours}-${i}`} value={i}>
                      Step {i + 1}/{testFireTemplate.steps.length} · {s.channel} · day {Math.round(s.delay_hours / 24)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3">
                This creates a one-shot sequence run flagged <code>test_mode=true</code>. It fires the selected step in ~60s (real email / SMS / Vapi call to the target).
              </div>
              <button
                type="submit"
                disabled={firing}
                data-testid="test-fire-submit"
                className="bg-neutral-900 text-white px-4 py-2.5 font-semibold hover:bg-neutral-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {firing ? <><RefreshCw className="w-4 h-4 animate-spin" /> Scheduling…</> : <><FlaskConical className="w-4 h-4" /> Schedule test-fire</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  const colors = {
    green:  "border-green-200 bg-green-50 text-green-700",
    amber:  "border-amber-200 bg-amber-50 text-amber-700",
    default:"border-neutral-200 bg-white text-neutral-900",
  };
  const cls = colors[accent] || colors.default;
  return (
    <div className={`border p-5 ${cls}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-mono opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

/**
 * Small enroll dropdown used on Lead + Client detail pages.
 * Shows only templates matching the target_type, and disables options
 * already enrolled for that target.
 */
export function EnrollSequenceButton({ targetId, targetType, onEnrolled }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [enrolledKeys, setEnrolledKeys] = useState(new Set());
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: r }] = await Promise.all([
        api.get("/sequences/templates"),
        api.get(`/sequences/runs`, { params: { target_type: targetType, target_id: targetId } }),
      ]);
      setTemplates((t.templates || []).filter((x) => x.scope === targetType));
      setEnrolledKeys(new Set((r.runs || []).filter((x) => ["active", "paused"].includes(x.status)).map((x) => x.template_key)));
    } catch (err) { toast.error(formatApiError(err)); }
  }, [targetId, targetType]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const enroll = async (key) => {
    setBusy(key);
    try {
      await api.post("/sequences/enroll", {
        template_key: key,
        target_id: targetId,
        target_type: targetType,
      });
      toast.success("Enrolled — first step queued.");
      setOpen(false);
      if (onEnrolled) onEnrolled();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  };

  return (
    <div className="relative inline-block">
      <button
        data-testid="enroll-sequence-btn"
        onClick={() => setOpen(!open)}
        className="lf-btn-outline text-sm flex items-center gap-2"
      >
        <Zap className="w-4 h-4" /> Enroll in sequence
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-neutral-200 shadow-lg z-20" data-testid="enroll-sequence-menu">
            {templates.length === 0 && (
              <div className="p-4 text-sm text-neutral-500">No templates available for {targetType}s.</div>
            )}
            {templates.map((t) => {
              const already = enrolledKeys.has(t.key);
              return (
                <button
                  key={t.key}
                  data-testid={`enroll-template-${t.key}`}
                  disabled={already || busy === t.key}
                  onClick={() => enroll(t.key)}
                  className="w-full text-left p-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="font-medium text-sm flex items-center justify-between gap-2">
                    {t.name}
                    {already && <span className="text-xs text-green-700 font-mono">enrolled</span>}
                    {busy === t.key && <span className="text-xs text-neutral-400">…</span>}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{t.steps.length} steps · {t.description.split(".")[0]}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
